import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Content translations for Privacy Policy
const content = {
  hr: {
    title: 'POLITIKA PRIVATNOSTI',
    lastUpdate: 'Posljednje ažuriranje: 02. 01. 2026.',
    backToHome: 'Natrag na početnu',
    sections: {
      s1: {
        title: '1. Uvod',
        p1: 'Ova Politika privatnosti opisuje kako se osobni podaci prikupljaju, koriste i obrađuju prilikom korištenja booking platforme za rezervaciju smještaja.',
        p2: 'Platforma djeluje kao tehnički sustav za upravljanje rezervacijama više smještajnih objekata različitih vlasnika. Ugovor o smještaju sklapa se isključivo između gosta i vlasnika objekta koji se rezervira.',
        important: 'Važno:',
        importantText: '👉 Voditelj obrade osobnih podataka razlikuje se ovisno o objektu koji rezervirate.'
      },
      s2: {
        title: '2. Uloge u obradi osobnih podataka (GDPR)',
        processor: 'Izvršitelj obrade podataka (Data Processor)',
        processorDesc: 'Tehnički sustav booking platforme razvija i održava:',
        processorNote: 'BOP djeluje isključivo kao izvršitelj obrade podataka, pružajući tehničku infrastrukturu za rezervacije i obradu podataka isključivo prema uputama voditelja obrade.',
        controller: 'Voditelj obrade podataka (Data Controller)',
        controllerDesc: 'Voditelj obrade osobnih podataka je vlasnik smještajnog objekta koji se rezervira. On određuje svrhu i način obrade osobnih podataka gostiju, sukladno važećim zakonima.'
      },
      s3: {
        title: '3. Popis vlasnika objekata (Voditelji obrade)',
        desc: 'Sljedeća tablica prikazuje vlasnike smještajnih objekata koji su odgovorni za obradu osobnih podataka gostiju:',
        noOwners: 'Informacije o vlasnicima trenutno nisu dostupne.',
        tableHeaders: { owner: 'Vlasnik / Firma', oib: 'OIB', address: 'Adresa', email: 'Kontakt email', properties: 'Objekti' }
      },
      s4: {
        title: '4. Koje osobne podatke prikupljamo',
        desc: 'Prilikom korištenja booking platforme mogu se prikupljati sljedeći podaci:',
        items: ['Ime i prezime gosta', 'Email adresa', 'Broj telefona', 'Podaci o identifikacijskom dokumentu (osobna iskaznica ili putovnica)', 'Datumi dolaska i odlaska', 'Broj gostiju', 'Podaci vezani uz rezervaciju', 'Podaci o plaćanju (obrađuje isključivo vanjski pružatelj plaćanja)']
      },
      s5: {
        title: '5. Svrha obrade osobnih podataka',
        desc: 'Osobni podaci obrađuju se isključivo u sljedeće svrhe:',
        items: ['Izvršenje rezervacije i ugovora o smještaju', 'Komunikacija vezana uz rezervaciju i boravak', 'Slanje potvrda i informacija o dolasku', 'Ispunjavanje zakonskih obveza (npr. prijava gostiju nadležnim tijelima)', 'Obrada plaćanja putem vanjskih pružatelja usluga']
      },
      s6: {
        title: '6. Pravna osnova obrade',
        desc: 'Obrada osobnih podataka temelji se na:',
        items: ['Izvršenju ugovora – obrada je nužna za realizaciju rezervacije', 'Zakonskoj obvezi – obveze prema važećim propisima (npr. prijava gostiju)', 'Legitimnom interesu – osiguravanje funkcionalnosti sustava i komunikacije s gostima']
      },
      s7: {
        title: '7. Dijeljenje osobnih podataka',
        desc: 'Osobni podaci mogu se dijeliti isključivo s:',
        items: ['Vlasnikom smještajnog objekta (voditelj obrade)', 'Stripe – pružatelj usluge online plaćanja (obrađuje podatke o karticama)', 'Beds24 – channel manager sustav za sinkronizaciju rezervacija', 'Resend – pružatelj usluge slanja transakcijskih emailova (potvrde rezervacija)', 'Nadležnim državnim tijelima – kada je to zakonski obavezno'],
        note: 'BOP / RentalWeb ne koristi osobne podatke u vlastite marketinške ili druge svrhe.'
      },
      s8: {
        title: '8. Razdoblje čuvanja podataka',
        desc: 'Osobni podaci čuvaju se:',
        items: ['onoliko dugo koliko je potrebno za izvršenje svrhe obrade', 'sukladno zakonskim obvezama voditelja obrade'],
        note: 'Uobičajeno razdoblje čuvanja podataka iznosi 2 do 5 godina nakon završetka boravka, ovisno o vrsti podatka i zakonskim zahtjevima.'
      },
      s9: {
        title: '9. Prava ispitanika',
        desc: 'Sukladno Općoj uredbi o zaštiti podataka (GDPR), imate pravo na:',
        items: ['pristup svojim osobnim podacima', 'ispravak netočnih ili nepotpunih podataka', 'brisanje osobnih podataka (kada je primjenjivo)', 'ograničenje obrade', 'prenosivost podataka', 'prigovor na obradu'],
        note: 'Za ostvarivanje svojih prava, obratite se vlasniku smještajnog objekta kod kojeg ste izvršili rezervaciju (naveden u tablici iznad).'
      },
      s10: {
        title: '10. Kontakt',
        desc: 'Za tehnička pitanja vezana uz funkcioniranje sustava:',
        note: 'Za pitanja vezana uz obradu osobnih podataka, molimo kontaktirajte vlasnika objekta kod kojeg ste rezervirali smještaj.'
      }
    }
  },
  en: {
    title: 'PRIVACY POLICY',
    lastUpdate: 'Last updated: January 2, 2026',
    backToHome: 'Back to home',
    sections: {
      s1: {
        title: '1. Introduction',
        p1: 'This Privacy Policy describes how personal data is collected, used, and processed when using the booking platform for accommodation reservations.',
        p2: 'The Platform operates as a technical system for managing reservations of multiple accommodation properties owned by different owners. The accommodation contract is concluded exclusively between the guest and the owner of the property being reserved.',
        important: 'Important:',
        importantText: '👉 The data controller varies depending on which property you book.'
      },
      s2: {
        title: '2. Roles in Personal Data Processing (GDPR)',
        processor: 'Data Processor',
        processorDesc: 'The technical booking platform system is developed and maintained by:',
        processorNote: 'BOP acts exclusively as a data processor, providing technical infrastructure for reservations and data processing solely according to the instructions of the data controller.',
        controller: 'Data Controller',
        controllerDesc: 'The data controller is the owner of the accommodation property being reserved. They determine the purpose and method of processing guest personal data, in accordance with applicable laws.'
      },
      s3: {
        title: '3. List of Property Owners (Data Controllers)',
        desc: 'The following table shows the owners of accommodation properties who are responsible for processing guest personal data:',
        noOwners: 'Owner information is currently not available.',
        tableHeaders: { owner: 'Owner / Company', oib: 'Tax ID', address: 'Address', email: 'Contact email', properties: 'Properties' }
      },
      s4: {
        title: '4. What Personal Data We Collect',
        desc: 'When using the booking platform, the following data may be collected:',
        items: ['Guest name', 'Email address', 'Phone number', 'Identity document data (ID card or passport)', 'Check-in and check-out dates', 'Number of guests', 'Reservation-related data', 'Payment data (processed exclusively by external payment provider)']
      },
      s5: {
        title: '5. Purpose of Personal Data Processing',
        desc: 'Personal data is processed exclusively for the following purposes:',
        items: ['Execution of reservation and accommodation contract', 'Communication related to reservation and stay', 'Sending confirmations and arrival information', 'Fulfillment of legal obligations (e.g., guest registration with authorities)', 'Payment processing through external service providers']
      },
      s6: {
        title: '6. Legal Basis for Processing',
        desc: 'Personal data processing is based on:',
        items: ['Contract execution – processing is necessary for reservation fulfillment', 'Legal obligation – obligations under applicable regulations (e.g., guest registration)', 'Legitimate interest – ensuring system functionality and guest communication']
      },
      s7: {
        title: '7. Sharing Personal Data',
        desc: 'Personal data may only be shared with:',
        items: ['Accommodation property owner (data controller)', 'Stripe – online payment service provider (processes card data)', 'Beds24 – channel manager system for reservation synchronization', 'Resend – transactional email service provider (booking confirmations)', 'Relevant government authorities – when legally required'],
        note: 'BOP / RentalWeb does not use personal data for its own marketing or other purposes.'
      },
      s8: {
        title: '8. Data Retention Period',
        desc: 'Personal data is retained:',
        items: ['as long as necessary for the processing purpose', 'in accordance with legal obligations of the data controller'],
        note: 'The usual data retention period is 2 to 5 years after the end of the stay, depending on the type of data and legal requirements.'
      },
      s9: {
        title: '9. Data Subject Rights',
        desc: 'Under the General Data Protection Regulation (GDPR), you have the right to:',
        items: ['access your personal data', 'rectification of inaccurate or incomplete data', 'erasure of personal data (where applicable)', 'restriction of processing', 'data portability', 'object to processing'],
        note: 'To exercise your rights, contact the property owner where you made your reservation (listed in the table above).'
      },
      s10: {
        title: '10. Contact',
        desc: 'For technical questions regarding system functionality:',
        note: 'For questions regarding personal data processing, please contact the property owner where you made your reservation.'
      }
    }
  },
  it: {
    title: 'INFORMATIVA SULLA PRIVACY',
    lastUpdate: 'Ultimo aggiornamento: 2 gennaio 2026',
    backToHome: 'Torna alla home',
    sections: {
      s1: {
        title: '1. Introduzione',
        p1: 'La presente Informativa sulla Privacy descrive come i dati personali vengono raccolti, utilizzati ed elaborati durante l\'utilizzo della piattaforma di prenotazione per la prenotazione di alloggi.',
        p2: 'La Piattaforma funziona come sistema tecnico per la gestione delle prenotazioni di più strutture ricettive di diversi proprietari. Il contratto di alloggio viene stipulato esclusivamente tra l\'ospite e il proprietario della struttura prenotata.',
        important: 'Importante:',
        importantText: '👉 Il titolare del trattamento dei dati personali varia a seconda della struttura prenotata.'
      },
      s2: {
        title: '2. Ruoli nel Trattamento dei Dati Personali (GDPR)',
        processor: 'Responsabile del Trattamento (Data Processor)',
        processorDesc: 'Il sistema tecnico della piattaforma di prenotazione è sviluppato e gestito da:',
        processorNote: 'BOP agisce esclusivamente come responsabile del trattamento dei dati, fornendo l\'infrastruttura tecnica per le prenotazioni e l\'elaborazione dei dati esclusivamente secondo le istruzioni del titolare del trattamento.',
        controller: 'Titolare del Trattamento (Data Controller)',
        controllerDesc: 'Il titolare del trattamento dei dati personali è il proprietario della struttura ricettiva prenotata. Egli determina le finalità e le modalità del trattamento dei dati personali degli ospiti, in conformità con le leggi vigenti.'
      },
      s3: {
        title: '3. Elenco dei Proprietari delle Strutture (Titolari del Trattamento)',
        desc: 'La seguente tabella mostra i proprietari delle strutture ricettive responsabili del trattamento dei dati personali degli ospiti:',
        noOwners: 'Le informazioni sui proprietari non sono attualmente disponibili.',
        tableHeaders: { owner: 'Proprietario / Azienda', oib: 'Codice Fiscale', address: 'Indirizzo', email: 'Email di contatto', properties: 'Strutture' }
      },
      s4: {
        title: '4. Quali Dati Personali Raccogliamo',
        desc: 'Durante l\'utilizzo della piattaforma di prenotazione possono essere raccolti i seguenti dati:',
        items: ['Nome e cognome dell\'ospite', 'Indirizzo email', 'Numero di telefono', 'Dati del documento d\'identità (carta d\'identità o passaporto)', 'Date di check-in e check-out', 'Numero di ospiti', 'Dati relativi alla prenotazione', 'Dati di pagamento (elaborati esclusivamente dal fornitore di servizi di pagamento esterno)']
      },
      s5: {
        title: '5. Finalità del Trattamento dei Dati Personali',
        desc: 'I dati personali vengono trattati esclusivamente per le seguenti finalità:',
        items: ['Esecuzione della prenotazione e del contratto di alloggio', 'Comunicazione relativa alla prenotazione e al soggiorno', 'Invio di conferme e informazioni sull\'arrivo', 'Adempimento degli obblighi di legge (es. registrazione degli ospiti presso le autorità)', 'Elaborazione dei pagamenti tramite fornitori di servizi esterni']
      },
      s6: {
        title: '6. Base Giuridica del Trattamento',
        desc: 'Il trattamento dei dati personali si basa su:',
        items: ['Esecuzione del contratto – il trattamento è necessario per l\'esecuzione della prenotazione', 'Obbligo legale – obblighi ai sensi delle normative vigenti (es. registrazione degli ospiti)', 'Interesse legittimo – garantire la funzionalità del sistema e la comunicazione con gli ospiti']
      },
      s7: {
        title: '7. Condivisione dei Dati Personali',
        desc: 'I dati personali possono essere condivisi esclusivamente con:',
        items: ['Proprietario della struttura ricettiva (titolare del trattamento)', 'Stripe – fornitore di servizi di pagamento online (elabora i dati delle carte)', 'Beds24 – sistema di channel manager per la sincronizzazione delle prenotazioni', 'Resend – fornitore di servizi email transazionali (conferme di prenotazione)', 'Autorità governative competenti – quando richiesto dalla legge'],
        note: 'BOP / RentalWeb non utilizza i dati personali per scopi di marketing o altri scopi propri.'
      },
      s8: {
        title: '8. Periodo di Conservazione dei Dati',
        desc: 'I dati personali vengono conservati:',
        items: ['per il tempo necessario all\'esecuzione della finalità del trattamento', 'in conformità con gli obblighi legali del titolare del trattamento'],
        note: 'Il periodo di conservazione abituale è da 2 a 5 anni dopo la fine del soggiorno, a seconda del tipo di dato e dei requisiti legali.'
      },
      s9: {
        title: '9. Diritti dell\'Interessato',
        desc: 'Ai sensi del Regolamento Generale sulla Protezione dei Dati (GDPR), hai il diritto di:',
        items: ['accesso ai tuoi dati personali', 'rettifica dei dati inesatti o incompleti', 'cancellazione dei dati personali (ove applicabile)', 'limitazione del trattamento', 'portabilità dei dati', 'opposizione al trattamento'],
        note: 'Per esercitare i tuoi diritti, contatta il proprietario della struttura presso cui hai effettuato la prenotazione (indicato nella tabella sopra).'
      },
      s10: {
        title: '10. Contatti',
        desc: 'Per domande tecniche relative al funzionamento del sistema:',
        note: 'Per domande relative al trattamento dei dati personali, contatta il proprietario della struttura presso cui hai effettuato la prenotazione.'
      }
    }
  },
  de: {
    title: 'DATENSCHUTZERKLÄRUNG',
    lastUpdate: 'Letzte Aktualisierung: 2. Januar 2026',
    backToHome: 'Zurück zur Startseite',
    sections: {
      s1: {
        title: '1. Einleitung',
        p1: 'Diese Datenschutzerklärung beschreibt, wie personenbezogene Daten bei der Nutzung der Buchungsplattform für Unterkunftsreservierungen erhoben, verwendet und verarbeitet werden.',
        p2: 'Die Plattform fungiert als technisches System zur Verwaltung von Reservierungen mehrerer Unterkunftsobjekte verschiedener Eigentümer. Der Beherbergungsvertrag wird ausschließlich zwischen dem Gast und dem Eigentümer des gebuchten Objekts geschlossen.',
        important: 'Wichtig:',
        importantText: '👉 Der Verantwortliche für die Verarbeitung personenbezogener Daten variiert je nach gebuchtem Objekt.'
      },
      s2: {
        title: '2. Rollen bei der Verarbeitung personenbezogener Daten (DSGVO)',
        processor: 'Auftragsverarbeiter (Data Processor)',
        processorDesc: 'Das technische System der Buchungsplattform wird entwickelt und betrieben von:',
        processorNote: 'BOP handelt ausschließlich als Auftragsverarbeiter und stellt die technische Infrastruktur für Reservierungen und Datenverarbeitung ausschließlich nach Anweisung des Verantwortlichen bereit.',
        controller: 'Verantwortlicher (Data Controller)',
        controllerDesc: 'Verantwortlicher für die Verarbeitung personenbezogener Daten ist der Eigentümer des gebuchten Unterkunftsobjekts. Er bestimmt den Zweck und die Mittel der Verarbeitung der personenbezogenen Daten der Gäste gemäß den geltenden Gesetzen.'
      },
      s3: {
        title: '3. Liste der Objekteigentümer (Verantwortliche)',
        desc: 'Die folgende Tabelle zeigt die Eigentümer der Unterkunftsobjekte, die für die Verarbeitung der personenbezogenen Daten der Gäste verantwortlich sind:',
        noOwners: 'Informationen zu den Eigentümern sind derzeit nicht verfügbar.',
        tableHeaders: { owner: 'Eigentümer / Unternehmen', oib: 'Steuernummer', address: 'Adresse', email: 'Kontakt-E-Mail', properties: 'Unterkünfte' }
      },
      s4: {
        title: '4. Welche personenbezogenen Daten wir erheben',
        desc: 'Bei der Nutzung der Buchungsplattform können folgende Daten erhoben werden:',
        items: ['Name des Gastes', 'E-Mail-Adresse', 'Telefonnummer', 'Ausweisdaten (Personalausweis oder Reisepass)', 'An- und Abreisedaten', 'Anzahl der Gäste', 'Reservierungsbezogene Daten', 'Zahlungsdaten (werden ausschließlich vom externen Zahlungsdienstleister verarbeitet)']
      },
      s5: {
        title: '5. Zweck der Verarbeitung personenbezogener Daten',
        desc: 'Personenbezogene Daten werden ausschließlich zu folgenden Zwecken verarbeitet:',
        items: ['Durchführung der Reservierung und des Beherbergungsvertrags', 'Kommunikation im Zusammenhang mit Reservierung und Aufenthalt', 'Versand von Bestätigungen und Ankunftsinformationen', 'Erfüllung gesetzlicher Pflichten (z.B. Meldung der Gäste bei Behörden)', 'Zahlungsabwicklung über externe Dienstleister']
      },
      s6: {
        title: '6. Rechtsgrundlage der Verarbeitung',
        desc: 'Die Verarbeitung personenbezogener Daten basiert auf:',
        items: ['Vertragserfüllung – die Verarbeitung ist für die Durchführung der Reservierung erforderlich', 'Gesetzliche Verpflichtung – Verpflichtungen nach geltendem Recht (z.B. Gästeregistrierung)', 'Berechtigtes Interesse – Gewährleistung der Systemfunktionalität und Kommunikation mit Gästen']
      },
      s7: {
        title: '7. Weitergabe personenbezogener Daten',
        desc: 'Personenbezogene Daten dürfen nur an folgende Parteien weitergegeben werden:',
        items: ['Eigentümer des Unterkunftsobjekts (Verantwortlicher)', 'Stripe – Online-Zahlungsdienstleister (verarbeitet Kartendaten)', 'Beds24 – Channel-Manager-System zur Reservierungssynchronisierung', 'Resend – Transaktions-E-Mail-Dienstleister (Buchungsbestätigungen)', 'Zuständige Behörden – wenn gesetzlich vorgeschrieben'],
        note: 'BOP / RentalWeb verwendet personenbezogene Daten nicht für eigene Marketing- oder andere Zwecke.'
      },
      s8: {
        title: '8. Speicherdauer',
        desc: 'Personenbezogene Daten werden aufbewahrt:',
        items: ['solange für den Verarbeitungszweck erforderlich', 'gemäß den gesetzlichen Aufbewahrungspflichten des Verantwortlichen'],
        note: 'Die übliche Speicherdauer beträgt 2 bis 5 Jahre nach Ende des Aufenthalts, abhängig von der Art der Daten und den gesetzlichen Anforderungen.'
      },
      s9: {
        title: '9. Rechte der betroffenen Person',
        desc: 'Gemäß der Datenschutz-Grundverordnung (DSGVO) haben Sie das Recht auf:',
        items: ['Auskunft über Ihre personenbezogenen Daten', 'Berichtigung unrichtiger oder unvollständiger Daten', 'Löschung personenbezogener Daten (sofern zutreffend)', 'Einschränkung der Verarbeitung', 'Datenübertragbarkeit', 'Widerspruch gegen die Verarbeitung'],
        note: 'Um Ihre Rechte auszuüben, wenden Sie sich an den Eigentümer des Objekts, bei dem Sie Ihre Reservierung vorgenommen haben (in der obigen Tabelle aufgeführt).'
      },
      s10: {
        title: '10. Kontakt',
        desc: 'Für technische Fragen zur Systemfunktionalität:',
        note: 'Für Fragen zur Verarbeitung personenbezogener Daten wenden Sie sich bitte an den Eigentümer des Objekts, bei dem Sie Ihre Reservierung vorgenommen haben.'
      }
    }
  }
};

const PrivacyPolicy = () => {
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get('roomId');
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentLang = i18n.language;
  const t = content[currentLang] || content.en;

  useEffect(() => {
    fetchOwners();
  }, [roomId]);

  const fetchOwners = async () => {
    try {
      // If roomId is provided, fetch only the owner for that specific room
      if (roomId) {
        const response = await axios.get(`${API}/owners/by-room/${roomId}`);
        if (response.data?.owner) {
          // Create a filtered owner object with only the specific room
          const filteredOwner = {
            ...response.data.owner,
            assigned_rooms: [{ room_id: roomId, room_name: response.data.room_name }]
          };
          setOwners([filteredOwner]);
        } else {
          setOwners([]);
        }
      } else {
        // Fetch all owners for the general page view
        const response = await axios.get(`${API}/owners/public`);
        setOwners(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching owners:', error);
      setOwners([]);
    } finally {
      setLoading(false);
    }
  };

  const getEntityTypeLabel = (type, lang) => {
    const labels = {
      private: { hr: 'Privatni iznajmljivač', en: 'Private Landlord', it: 'Locatore privato', de: 'Privatvermieter' },
      trade: { hr: 'Obrt', en: 'Sole Proprietorship', it: 'Ditta individuale', de: 'Einzelunternehmen' },
      company: { hr: 'Trgovačko društvo', en: 'Company', it: 'Società', de: 'Gesellschaft' }
    };
    return labels[type]?.[lang] || labels[type]?.en || type;
  };

  const OwnersTable = () => (
    loading ? (
      <div className="text-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
      </div>
    ) : owners.length > 0 ? (
      <div className="overflow-x-auto my-6">
        <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t.sections.s3.tableHeaders.owner}</th>
              <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t.sections.s3.tableHeaders.oib}</th>
              <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t.sections.s3.tableHeaders.address}</th>
              <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t.sections.s3.tableHeaders.email}</th>
              <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t.sections.s3.tableHeaders.properties}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {owners.map((owner, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <div className="font-medium">{owner.name}</div>
                  <div className="text-xs text-gray-500">{getEntityTypeLabel(owner.entity_type, currentLang)}</div>
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{owner.oib && owner.oib.trim() ? owner.oib : '-'}</td>
                <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{owner.full_address && owner.full_address.trim() ? owner.full_address : '-'}</td>
                <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{owner.email}</td>
                <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {owner.assigned_rooms && owner.assigned_rooms.length > 0 ? (
                    <ul className="list-disc list-inside">
                      {owner.assigned_rooms.map((room, idx) => (<li key={idx}>{room.room_name}</li>))}
                    </ul>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <p className="text-gray-500 italic py-4 my-6">{t.sections.s3.noOwners}</p>
    )
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)', fontFamily: 'var(--font-body)' }}>
      <Header />
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-4xl">
        <h1 className="text-3xl sm:text-4xl md:text-5xl mb-4" style={{ fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)', color: 'var(--color-text-primary)' }}>{t.title}</h1>
        <p className="text-sm mb-8" style={{ color: 'var(--color-text-muted)' }}>{t.lastUpdate}</p>

        <div className="space-y-8 bg-white rounded-lg shadow-sm p-6 sm:p-8 prose prose-stone max-w-none">
          {/* Section 1 */}
          <section>
            <h2 className="text-xl sm:text-2xl mb-3" style={{ fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)', color: 'var(--color-text-primary)' }}>{t.sections.s1.title}</h2>
            <p style={{ color: 'var(--color-text-secondary)' }}>{t.sections.s1.p1}</p>
            <p style={{ color: 'var(--color-text-secondary)' }}>{t.sections.s1.p2}</p>
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 my-4">
              <p className="font-medium text-amber-800"><strong>{t.sections.s1.important}</strong><br/>{t.sections.s1.importantText}</p>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl sm:text-2xl mb-3" style={{ fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)', color: 'var(--color-text-primary)' }}>{t.sections.s2.title}</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>{t.sections.s2.processor}</h3>
              <p style={{ color: 'var(--color-text-secondary)' }} className="mb-3">{t.sections.s2.processorDesc}</p>
              <div className="bg-white p-4 rounded border">
                <p className="font-bold text-lg" style={{ color: 'var(--color-primary)' }}>RentalWeb</p>
                <p className="text-sm text-gray-500 mb-2">(trgovački naziv)</p>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  <strong>BOP, obrt za izradu web stranica, vl. Kornelia Šokota</strong><br/>
                  Krešimirova 20, 51000 Rijeka, Hrvatska<br/>
                  OIB: 78058083953<br/>
                  Email: info@rentalweb.eu
                </p>
              </div>
              <p className="mt-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{t.sections.s2.processorNote}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>{t.sections.s2.controller}</h3>
              <p style={{ color: 'var(--color-text-secondary)' }}>{t.sections.s2.controllerDesc}</p>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl sm:text-2xl mb-3" style={{ fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)', color: 'var(--color-text-primary)' }}>{t.sections.s3.title}</h2>
            <p style={{ color: 'var(--color-text-secondary)' }}>{t.sections.s3.desc}</p>
            <OwnersTable />
          </section>

          {/* Sections 4-10 */}
          {['s4', 's5', 's6', 's7', 's8', 's9'].map((key) => (
            <section key={key}>
              <h2 className="text-xl sm:text-2xl mb-3" style={{ fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)', color: 'var(--color-text-primary)' }}>{t.sections[key].title}</h2>
              <p style={{ color: 'var(--color-text-secondary)' }} className="mb-3">{t.sections[key].desc}</p>
              <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                {t.sections[key].items.map((item, i) => (<li key={i} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />))}
              </ul>
              {t.sections[key].note && (
                key === 's9' ? (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4"><p className="text-blue-800">{t.sections[key].note}</p></div>
                ) : (
                  <p className="mt-3 text-sm italic" style={{ color: 'var(--color-text-muted)' }}>{t.sections[key].note}</p>
                )
              )}
            </section>
          ))}

          {/* Section 10 */}
          <section>
            <h2 className="text-xl sm:text-2xl mb-3" style={{ fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)', color: 'var(--color-text-primary)' }}>{t.sections.s10.title}</h2>
            <p style={{ color: 'var(--color-text-secondary)' }} className="mb-3">{t.sections.s10.desc}</p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="font-bold" style={{ color: 'var(--color-primary)' }}>RentalWeb</p>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                BOP, obrt za izradu web stranica, vl. Kornelia Šokota<br/>
                Email: <a href="mailto:info@rentalweb.eu" className="text-blue-600 hover:underline">info@rentalweb.eu</a>
              </p>
            </div>
            <p style={{ color: 'var(--color-text-secondary)' }}>{t.sections.s10.note}</p>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm hover:underline" style={{ color: 'var(--color-primary)' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            {t.backToHome}
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
